import { CareCallCanaryDoc } from 'src/app/models/care-call-guide.model';

/**
 * The canary INSIDE the clinic canary: which MAs run BRD-CLIN-2026-006 at an onboarded clinic.
 * =========================================================================================
 * `clients/{id}.featureToggles.isChfCareCallAlertsEnabled` onboards a whole CLINIC, and that was
 * the entire gate until Luis Mata narrowed the rollout (2026-09-08): *"El canario de Emory es mas
 * reducido todavia Yvette Paz, Jennifer Arney, Alana Bessera, Kyla Arguilez — esas enfermeras usan
 * el protocolo CHF nuevo en los pacientes de Emory"*. Emory has more MAs than those four, and the
 * rest are to keep running the form exactly as it was.
 *
 * A clinic toggle cannot say that, so a second document does: `CHF_CARE_CALL_CANARY_DOC.user_ids`,
 * written by welby-firestore `maintenanceScripts/sprint-9-11-26/EnableChfCareCallCanaryUsers`. The two are
 * ANDed — clinic AND caller — and the AND gates BOTH halves, rendering and routing, so a
 * non-canary MA at Emory is never asked a §4.1 question and never routes one.
 *
 * WHY user_id AND NOT EMAIL. The seed resolves people to ids once, where it can refuse an ambiguous
 * or misspelled name loudly; matching an address here would put string handling (case, plus-
 * addressing, a changed surname) between a clinician and their protocol. The id is also what
 * `authService.user` carries on every screen.
 *
 * FAILS CLOSED, in every direction: a missing document, an absent or non-array `user_ids`, an empty
 * list, a read error, and a caller with no `user_id` all resolve to `false`, which is the pre-BRD
 * form. Being wrong that way costs four MAs one more day on the old form; failing open puts a
 * clinician on a protocol they were never trained on.
 *
 * PURE, so the membership rule can be tested without Firestore — the same split the sibling
 * `scopeGuideToClinic` uses.
 */

/**
 * The document the welby-firestore seed owns. Changing this changes `CANARY_DOC_PATH` and the
 * matching `firestore.rules` block there too.
 *
 * FOUR SEGMENTS, NOT TWO, and the depth is the access control rather than a naming taste. At
 * `welby_configuration/chf_care_call_canary` the document fell under that collection's wildcard
 * rule, which grants read AND WRITE to any authenticated user; rules are OR-ed, so no stricter rule
 * on the single doc could have taken the write back. Any account could have added itself to a
 * signed clinical protocol, or emptied `user_ids` — which, because the gate below fails closed,
 * would have been indistinguishable from the feature being switched off. The wildcard is not
 * recursive, so one collection deeper the only rule that applies is its own block, where a client can
 * update the document only as an admin who also holds `isFeatureFlagsManager` (the feature flags console).
 */
export const CHF_CARE_CALL_CANARY_DOC = 'welby_configuration/care_call_canaries/canaries/chf_care_call';

/**
 * Is this caller one of the MAs the canary names?
 *
 * @param doc     the document as Firestore emitted it, or null/undefined when it does not exist
 * @param userId  `authService.user?.user_id`
 */
export const isCareCallCanaryUser = (doc: CareCallCanaryDoc | null | undefined, userId: string | null | undefined): boolean => {
  if (!userId) {
    return false;
  }
  const ids = doc?.user_ids;
  if (!Array.isArray(ids)) {
    return false;
  }
  // Explicitly typed and trimmed rather than `ids.includes(userId)`: the document can be edited by
  // hand in the console, and a non-string entry or a stray space must fail to match rather than
  // match by coercion or throw.
  return ids.some((id) => typeof id === 'string' && id.trim() === userId);
};

/**
 * Sibling of `CHF_CARE_CALL_CANARY_DOC`, in the same collection and under the same
 * rule (admin + `isFeatureFlagsManager` to update, nobody to create). Gates something different:
 * not how `chf_ma` routes, but whether the Care Management Activation guide is OFFERED at all.
 *
 * It has to be a canary, and not just a seed, because the guide DOCUMENT is global — one
 * `activation_ma` for all 40 clinics. Seeding it without a per-user gate would hand every MA a
 * form that product has only opened to two people.
 *
 * ⚠️ The `firestore.rules` block that covers this path is a wildcard over `{canaryId}`, so this
 * sibling is covered without a rules change — but that block is not in welby-firestore's `main`
 * yet. A four-segment path with no matching rule is DENIED by default, reads included, which the
 * `catchError` below turns into `false`. Until it is deployed the canary fails closed for
 * everybody, and that is indistinguishable from "the feature does not work".
 */
export const ACTIVATION_GUIDE_CANARY_DOC = 'welby_configuration/care_call_canaries/canaries/activation_guide';

/**
 * The one spelling of an address both sides compare on.
 *
 * Case and surrounding whitespace only. Deliberately NOT gmail-style canonicalisation:
 * `hilary.bertolucci+admin@getwelby.com` and `hilary.bertolucci@getwelby.com` are SEPARATE Welby
 * accounts with separate uids — dev's Hilary is the `+admin` and `+rn` pair and has no plain
 * address at all — so folding plus-addressing away would canary an account nobody named, and
 * stripping dots would do the same to `lisa.waldman@` vs a hypothetical `lisawaldman@`.
 *
 * The seed writes addresses already in this form, so the stored document is canonical; each stored
 * entry is normalized again on read because the document is editable by hand in the console, where
 * a capital or a trailing space costs nothing to introduce and would otherwise silently drop that
 * person from the canary.
 */
export const normalizeCanaryEmail = (email: string | null | undefined): string => (typeof email === 'string' ? email.trim().toLowerCase() : '');

/**
 * Does this caller get offered the Care Management Activation guide?
 *
 * `enabled` AND membership. It is a separate function rather than a flag added to
 * `isCareCallCanaryUser` because the CHF document does not carry `enabled`, and changing that
 * function would move the gate of another team's feature.
 *
 * MATCHES ON ADDRESS, WHERE THE CHF SIBLING MATCHES ON UID, and the divergence is the point rather
 * than drift. A uid is scoped to a Firebase project: the same person is one string in `welby-dev`
 * and a different one in `welby-prod`, so a uid-keyed canary can only be produced by re-running the
 * seed per project, and what it stores is five opaque strings that nobody can read back against the
 * two names product signed off on. That is tolerable for `chf_care_call`, which is already seeded
 * and belongs to another team. It was not worth carrying into a second document.
 *
 * The guardrail that uid-keying bought is NOT lost: the seed still resolves every address against
 * `users` before writing it and refuses the whole run on one that matches zero or several staff
 * accounts. Only the stored key and the comparison moved.
 *
 * `enabled` ABSENT counts as `true`: `chf_care_call` is already seeded without the field, and
 * treating its absence as `false` would switch that feature off. `null` counts as true for the same
 * reason — that is what clearing the field in the console produces.
 *
 * ANYTHING ELSE CLOSES IT, including the STRING `"false"`. The document is editable by hand in the
 * Firestore console, where the type comes from a dropdown, and `"false" !== false` would have left
 * the gate open on a document whose author plainly meant to shut it.
 *
 * FAILS CLOSED on every other path too, `user_emails` absent included — which is exactly the shape
 * a document seeded by the older, uid-keyed script has. That direction is the safe one: the guide
 * is simply not offered until the canary is re-seeded.
 *
 * This is only two of the three gates. The third — `patient.status.active === true` — is per
 * PATIENT and lives in `CareCallGuideHomeComponent`, because nothing about a patient is visible
 * from here.
 *
 * @param doc    the document as Firestore emitted it, or null/undefined when it does not exist
 * @param email  `authService.user?.email` — the address on the caller's `users` document, which is
 *               the same field the seed resolves against
 */
export const isActivationCanaryUser = (doc: CareCallCanaryDoc | null | undefined, email: string | null | undefined): boolean => {
  const enabled = doc?.enabled;
  const switchedOn = enabled === undefined || enabled === null || enabled === true;
  if (!switchedOn) {
    return false;
  }
  const wanted = normalizeCanaryEmail(email);
  if (!wanted) {
    return false;
  }
  const emails = doc?.user_emails;
  if (!Array.isArray(emails)) {
    return false;
  }
  // Typed per entry rather than `emails.includes(wanted)`: a hand-edited document can hold a
  // number or a nested value, which must fail to match rather than match by coercion or throw.
  return emails.some((stored) => typeof stored === 'string' && normalizeCanaryEmail(stored) === wanted);
};
